import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Trash2, Pencil, Eye, EyeOff } from "lucide-react";
import ConfirmModal from "../../components/ConfirmModal";
import { useAdmin } from "../../context/AdminContext";

export default function CreateAdminAccount() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("editor");
  const [status, setStatus] = useState("active");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState("");
   const [alert, setAlert] = useState({ message: "", type: "", visible: false });

  const [admins, setAdmins] = useState([]);
  const [formOpen, setFormOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [idToDelete , setIdToDelete] = useState(null);
  const [, setEditingEmail] = useState("");
  const [editingAdminId, setEditingAdminId] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { admin, setAdmin } = useAdmin();

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => setAlert({ message: "", type: "", visible: false }), 3000);
  };

  useEffect(() => {
    const fetchCurrentAdmin = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/profile`, {
          credentials: "include",
        });

        // This will log the raw Response object

        if (!res.ok) throw new Error("Not authorized");

        // Now parse the JSON body
        const data = await res.json();

        if (data.role !== "superadmin") {
          navigate("/admin/dashboard");
        }
      } catch (err) {
        console.error("Error checking admin session", err);
        navigate("/admin/dashboard");
      }
    };

    fetchCurrentAdmin();
  }, [navigate, BASE_URL]);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch admins: ${errorText}`);
      }

      const data = await res.json();

      setAdmins(data);
    } catch (err) {
      console.error("Error fetching admin users:", err);
      setAdmins([]); // reset state if error occurs
    }
  }, [BASE_URL, setAdmins]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();

    const newAdmin = { name, email, password, role, status };
    const isSelfUpdate = isEditing && editingAdminId === admin?._id;

    // Remove password from update if empty
    if (isEditing && !password) {
      delete newAdmin.password;
    }

    const endpoint = isEditing
      ? `${BASE_URL}/api/admin/${editingAdminId}` // use _id for update
      : `${BASE_URL}/api/admin/create-admin`;

    const method = isEditing ? "PATCH" : "POST";

    const successMessage = isEditing
      ? "Admin account updated successfully."
      : "Admin account created successfully.";

    try {
      const response = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newAdmin),
      });

      const data = await response.json();

      if (!response.ok)
        throw new Error(
          data.message || `Failed to ${isEditing ? "update" : "create"} admin`
        );

      showAlert(successMessage, "success");

      // If updating self, update current session data
      if (isSelfUpdate) {
        setAdmin((prev) => ({
          ...prev,
          name,
          role,
          status,
        }));
      }

      fetchAdmins(); // Refresh admin list

      // Reset form state
      setEmail("");
      setName("");
      setPassword("");
      setRole("editor");
      setStatus("active");
      setIsEditing(false);
      setEditingAdminId("");

    } catch (err) {
      console.error("Admin create/update error:", err);
      showAlert(err.message, "error");
    }
  };

  const triggerDelete = (targetId) => {
    if (!targetId) return;

    const targetAdmin = admins.find((admin) => admin._id === targetId);
    if (!targetAdmin) return;

    const isTargetSuperadmin = targetAdmin.role === "superadmin";
    const totalSuperadmins = admins.filter((admin) => admin.role === "superadmin").length;

    if (isTargetSuperadmin && totalSuperadmins === 1) {
      showAlert("Cannot delete the last remaining superadmin.", "error");
      return;
    }

    setIdToDelete(targetId);
    setEmailToDelete(targetAdmin.email);
    setConfirmOpen(true);
  };

  const performDelete = async (targetId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/${targetId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete admin");

      setAdmins((prev) => prev.filter((admin) => admin._id !== targetId));
      showAlert("Admin deleted successfully", "success");
    } catch (err) {
      console.error("Error deleting admin:", err);
      showAlert("Failed to delete admin", "error");
    }
  };

  const handleEdit = (admin) => {
    setIsEditing(true);
    setFormOpen(true);

    // Pre-fill fields
    setEmail(admin.email);
    setName(admin.name || "");
    setRole(admin.role);
    setStatus(admin.status || "active");

    setPassword(""); 

    // Track original admin ID for update reference
    setEditingAdminId(admin._id);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      {/* ✅ Banner Notification */}
      {alert.visible && (
        <div
          className={`mb-4 px-4 py-3 rounded text-sm ${
            alert.type === "success"
              ? "bg-green-100 text-green-800"
              : alert.type === "error"
              ? "bg-red-100 text-red-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {alert.message}
        </div>
      )}
      {/* Collapsible Header */}
      <div
        className="flex justify-between items-center bg-gray-100 border rounded px-4 py-3 cursor-pointer mb-6"
        onClick={() => setFormOpen(!formOpen)}
      >
        <h2 className="text-xl font-semibold">
          {isEditing ? "Edit Admin Account" : "Create Admin Account"}
        </h2>
        {formOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {/* Create/Edit Form */}
      {formOpen && (
        <div className="bg-white rounded shadow p-6 mb-10">
          <form onSubmit={handleCreateOrUpdate} className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 font-medium">
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                className="w-full border rounded px-3 py-2"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                disabled={isEditing}
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={name}
                required
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                Password {!isEditing && <span className="text-red-600">*</span>}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border rounded px-3 py-2 pr-10"
                  value={password}
                  required={!isEditing} // Only required if not editing
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div title="Superadmins cannot change their own role">
              <label className="block mb-1 font-medium">Role</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={isEditing && email === admin.email && admin.role === "superadmin"}
              >
                <option value="editor">Editor</option>
                <option value="manager">Manager</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>

            <div title="Superadmins cannot change their own status">
              <label className="block mb-1 font-medium">Status</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={isEditing && email === admin.email && admin.role === "superadmin"}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="md:col-span-3 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-500 w-full sm:w-auto"
              >
                {isEditing ? "Update Account" : "Create Account"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingEmail("");
                  setEmail("");
                  setName("");
                  setPassword("");
                  setRole("editor");
                  setStatus("active");
                }}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded hover:bg-gray-400 w-full sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Admins Table */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100 text-gray-700 text-left">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {admins.map((admin) => {
              const isCurrentUser = admin.email;
              const isCurrentSuperAdmin = admin.role === "superadmin" && isCurrentUser;

              return (
                <tr key={admin.email} className="align-middle">
                  <td className="px-4 py-2 align-middle">{admin.email}</td>
                  <td className="px-4 py-2 align-middle">{admin.name || "—"}</td>

                  <td className="px-4 py-2 align-middle">
                    <div className="flex items-center gap-2 capitalize">
                      {admin.role}
                      {admin.role === "superadmin" && (
                        <span
                          title="Super Admin account cannot be deleted"
                          className="text-yellow-600 cursor-help"
                        >
                          🔒
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-2 align-middle capitalize">{admin.status || "active"}</td>

                  <td className="px-4 py-2 align-middle">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-blue-600 hover:underline text-sm mr-2 flex items-center gap-1"
                        onClick={() => handleEdit(admin)}
                      >
                        <Pencil size={14} /> Edit
                      </button>

                      {!isCurrentSuperAdmin && (
                        <button
                          className="text-red-600 hover:underline text-sm flex items-center gap-1"
                          onClick={() => triggerDelete(admin._id)}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {admins.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-4 text-gray-500 italic">
                  No admin accounts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <ConfirmModal
        isOpen={confirmOpen}
        title="Delete Admin Account"
        message={`Are you sure you want to delete "${emailToDelete}"?`}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          performDelete(idToDelete);
          setConfirmOpen(false);
        }}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />

    </div>
  );
}
